import Link from 'next/link';

import { Accordion, Box, Card, Icon } from '@zillow/constellation-10';
import { IconChevronDownFilled } from '@zillow/constellation-icons';

import { cardStyles } from '../../styles/propertyCardTable';

import {
  cardHeaderRowStyles,
  cardPanelStyles,
  linkedHeaderContentStyles,
  linkedHeaderRowStyles,
  linkedHeaderStyles,
  spacerIconStyles,
  trailingControlStyles,
} from './styles';

import type { SystemStyleObject } from '@/styled-system/types';
import type { ComponentProps, ReactNode } from 'react';

type LinkHref = ComponentProps<typeof Link>['href'];

type BaseProps = {
  header: ReactNode;
  headerCss: SystemStyleObject;
  // Rendered beside the header rather than inside it, so it never nests in the trigger or the link.
  trailingControl?: ReactNode;
  'data-testid'?: string;
};

type ExpandableProps = BaseProps & {
  variant: 'expandable';
  children: ReactNode;
  expandTriggerTestId?: string;
};

// A link card without an `href` yet renders inert rather than falling back to expanding.
type LinkProps = BaseProps & {
  variant: 'link';
  href?: LinkHref;
  linkLabel: string;
};

type Props = ExpandableProps | LinkProps;

/**
 * A card for AccordionCardList: expands into `children`, or links the whole card to `href`.
 */
export const AccordionCard = (props: Props) => {
  const { header, headerCss, trailingControl, 'data-testid': dataTestId } = props;
  const href = props.variant === 'link' ? props.href : undefined;

  return (
    <Card.Root
      data-testid={dataTestId}
      tone="neutral"
      outlined={true}
      elevated={false}
      interactive={href != null}
      css={cardStyles}
    >
      {props.variant === 'link' && href != null && (
        <Card.BlockAnchor asChild aria-hidden={undefined} aria-label={props.linkLabel || 'Property'} tabIndex={0}>
          <Link href={href} />
        </Card.BlockAnchor>
      )}
      <Box css={props.variant === 'link' ? linkedHeaderRowStyles : cardHeaderRowStyles}>
        {props.variant === 'link' ? (
          <Box css={{ ...linkedHeaderStyles, ...headerCss }}>
            {/* Holds the expand chevron's space so linked cards align with expandable ones. */}
            <Icon aria-hidden={true} css={spacerIconStyles}>
              <IconChevronDownFilled />
            </Icon>
            <Box css={linkedHeaderContentStyles}>{header}</Box>
          </Box>
        ) : (
          <Accordion.Header data-testid={props.expandTriggerTestId} css={headerCss}>
            {header}
          </Accordion.Header>
        )}
        {trailingControl && <Box css={trailingControlStyles}>{trailingControl}</Box>}
      </Box>
      {props.variant === 'expandable' && <Accordion.Panel css={cardPanelStyles}>{props.children}</Accordion.Panel>}
    </Card.Root>
  );
};

export default AccordionCard;
